import {
  AnimatedCard,
  AnimatedCardProps,
} from "@/shared/base/components/AnimatedCard/AnimatedCard";
import { InventoryType } from "@/global/store/data/types";
import { Box, Button, Divider, Stack, Typography } from "@mui/material";
import { getUSCurrency } from "@/global/utils/getUSCurrency";
import { inventoryTypeUIDs } from "@/global/config/constants";
import { AdaListItem, InventoryTag } from "./inventoryCard.helpers";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { useTeamConfig } from "@/global/hooks/useTeamConfig";

interface Props {
  cardVariant?: AnimatedCardProps["variant"];
  inventory: InventoryType;
  isSelected?: boolean;
  isDisabled?: boolean;
  isDisabledHoverAnim?: boolean;
  isFooterHidden?: boolean;
  onSelect?: () => void;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  onRender?: (el: HTMLElement) => void;
  style?: AnimatedCardProps["style"];
  hideViewSeatButton?: boolean;
  onClickViewSeat?: (section: string) => void;
}

export function SectionLevelInventoryCard(props: Props) {
  const inventory = props.inventory as any;
  const teamConfig = useTeamConfig();

  let title = `Section ${inventory.displaySectionName || inventory.sectionName}`; // prettier-ignore

  if (inventory.inventoryType == "ADA") {
    title = `Section ${inventory.displaySectionName || inventory.sectionName}, Row ${inventory.rowName}, Seat ${inventory?.seatNumber}`; // prettier-ignore
  }

  const { sm: smDown } = useCssMediaQuery("down");

  return (
    <AnimatedCard
      variant={props?.cardVariant}
      style={props?.style}
      onRender={props?.onRender}
      onMouseEnter={props.onMouseEnter}
      onMouseLeave={props.onMouseLeave}
      borderRadius={"10px"}
      footerHeight={!props.hideViewSeatButton ? "95px" : "70px"}
      isSelected={props?.isSelected}
      isDisabled={props?.isDisabled}
      isDisabledHoverAnim={props?.isDisabledHoverAnim}
      isFooterHidden={props?.isFooterHidden}
      renderBody={() => {
        return (
          <Box onClick={props.onSelect}>
            <Stack
              sx={{
                flexDirection: "row",
                position: "absolute",
                top: 0,
                right: "8px",
                zIndex: 1,
                transform: "translate(0%, -50%) scale(1)",
                overflow: "hidden",
                gap: 1,
              }}
            >
              {inventory?.containsAisle ? (
                <InventoryTag value={"Aisle Seat"} />
              ) : null}

              {inventory.priceCodeTag ? (
                <InventoryTag value={inventory.priceCodeTag} />
              ) : null}

              {inventory.sectionNameTag ? (
                <InventoryTag value={inventory.sectionNameTag} />
              ) : null}
            </Stack>

            <Box p={2} py={2.2}>
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                width={"100%"}
              >
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color="text.primary"
                  fontSize={smDown ? "0.75rem" : "0.875rem"}
                >
                  {title}
                </Typography>
                <Typography
                  variant="body2"
                  fontWeight={700}
                  color="text.primary"
                  fontSize={smDown ? "0.75rem" : "0.875rem"}
                >
                  {getUSCurrency(
                    Number(inventory.allInPrice || inventory.basePrice) *
                      inventory.seatCount
                  )}
                </Typography>
              </Stack>
            </Box>
            <Divider />

            <Stack padding={"1rem"}>
              {inventory.inventoryType === inventoryTypeUIDs.ada && props ? (
                <AdaListItem
                  name="Seat Type"
                  value={inventory?.seatType as string}
                  variant={
                    inventory?.seatType == "WHEELCHAIR" ? "blue" : "orange"
                  }
                />
              ) : null}

              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
                gap={"0.5rem"}
              >
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontSize={smDown ? "0.75rem" : "0.9rem"}
                  fontWeight={"500"}
                >
                  {teamConfig?.languages?.purchaseSeats
                    ?.inventoryCardPricePerSeatLabel || "Price Per Seat"}
                  :
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontSize={smDown ? "0.75rem" : "0.9rem"}
                  fontWeight={"500"}
                >
                  {getUSCurrency(
                    Number(inventory.allInPrice || inventory.basePrice)
                  )}
                </Typography>
              </Box>
              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
                gap={"0.5rem"}
              >
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontSize={smDown ? "0.75rem" : "0.9rem"}
                  fontWeight={"500"}
                >
                  Total Price:
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  fontSize={smDown ? "0.75rem" : "0.9rem"}
                  fontWeight={"500"}
                >
                  {getUSCurrency(
                    Number(inventory.allInPrice || inventory.basePrice) *
                      inventory.seatCount
                  )}
                </Typography>
              </Box>
            </Stack>
          </Box>
        );
      }}
      renderFooter={() => {
        return (
          <Stack width={"100%"} p={2} gap={0.7}>
            <Button
              variant={props.isSelected ? "outlined" : "contained"}
              onClick={props.onSelect}
              sx={{ width: "100%" }}
            >
              {props.isSelected ? "Unselect" : "Select +"}
            </Button>

            {!props.hideViewSeatButton ? (
              <Button
                variant="outlined"
                sx={{ width: "100%" }}
                onClick={() => {
                  const section =
                    inventory?.mapSectionName || inventory?.sectionName;
                  props?.onClickViewSeat?.(section);
                }}
              >
                View Seat
              </Button>
            ) : null}
          </Stack>
        );
      }}
    />
  );
}
